import { EventType, type EventWithLocations, type Location } from "@/components/RegistrationProcess/types/events.ts";
import { PHYSICAL_LOCATIONS_MODE, VIRTUAL_LOCATION } from "@/constants";

const formatSinglePhysicalLocation = (loc?: Location): string => {
  const parts = [loc?.city?.city, loc?.city?.country?.name].filter(
    (v): v is string => typeof v === "string" && v.length > 0,
  );

  return parts.join(", ");
};

const formatFirstPhysicalLocation = (event: EventWithLocations): string => {
  const locations = event.event_locations;
  if (!locations?.length) {
    return "";
  }
  return formatSinglePhysicalLocation(locations[0]);
};

const formatPhysicalLocationsJoined = (event: EventWithLocations): string => {
  const locations = event.event_locations;
  if (!locations?.length) {
    return "";
  }

  return locations
    .map((loc) => formatSinglePhysicalLocation(loc))
    .filter((segment) => segment.length > 0)
    .join(" | ");
};

type PhysicalLocationsMode = (typeof PHYSICAL_LOCATIONS_MODE)[keyof typeof PHYSICAL_LOCATIONS_MODE];

/**
 * Location line for Configurator excursion cards (included/optional/detail) and legacy excursion UI.
 * Online events use `online_location`, with fallback when unset.
 * @param multiplePhysicalLocations - `first` (default): only the first row; `join`: all rows separated by " | " (optional excursions list).
 */
export const getEventDisplayLocation = (
  event: Partial<EventWithLocations> | null | undefined,
  options?: { multiplePhysicalLocations?: PhysicalLocationsMode },
): string => {
  if (!event) {
    return "";
  }

  if (event.type === EventType.ONLINE) {
    const text = event.online_location?.trim();
    if (text) {
      return text;
    }
    return VIRTUAL_LOCATION;
  }

  const ev = event as EventWithLocations;
  const mode = options?.multiplePhysicalLocations ?? PHYSICAL_LOCATIONS_MODE.first;
  if (mode === PHYSICAL_LOCATIONS_MODE.join) {
    return formatPhysicalLocationsJoined(ev);
  }
  return formatFirstPhysicalLocation(ev);
};
